import {
  useMcpPrompts,
  useMcpPromptsWithCapabilities,
  useMcpResources,
  useMcpResourcesWithCapabilities,
  useMcpTools,
  useMcpToolsWithCapabilities,
} from "@/lib/hooks/useMcpInspector";
import { getProjectQueryOptions } from "@/lib/hooks/useProjects";
import { useMcpSessionStore } from "@/stores/mcpSessionStore";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export function useCompactInspectorData(projectId: string) {
  // Load project to determine deployment status
  const { data: projectData, isLoading: projectLoading } = useQuery(
    getProjectQueryOptions(projectId),
  );

  const project = projectData?.project;
  const isDeployed = useMemo(() => {
    if (!project?.latestDeployment) {
      return false;
    }
    // Treat deployment as live when status is complete, regardless of URL presence
    return project.latestDeployment.status === "complete";
  }, [project]);

  const session = useMcpSessionStore((state) => state.getSession(projectId));

  // Try capability-aware hooks first, fallback to basic hooks if issues
  const toolsWithCapabilities = useMcpToolsWithCapabilities(
    projectId,
    isDeployed,
  );
  const resourcesWithCapabilities = useMcpResourcesWithCapabilities(
    projectId,
    isDeployed,
  );
  const promptsWithCapabilities = useMcpPromptsWithCapabilities(
    projectId,
    isDeployed,
  );

  // Fallback hooks (original behavior)
  const fallbackEnabled = isDeployed && session.status === "connected";
  const toolsFallback = useMcpTools(projectId, fallbackEnabled);
  const resourcesFallback = useMcpResources(projectId, fallbackEnabled);
  const promptsFallback = useMcpPrompts(projectId, fallbackEnabled);

  // Debug logging for troubleshooting
  const debugInfo = useMemo(() => {
    if (process.env.NODE_ENV === "development") {
      return {
        sessionStatus: session.status,
        sessionError: session.error,
        isDeployed,
        initData: toolsWithCapabilities.initData,
        initLoading: toolsWithCapabilities.isInitializing,
        initError: toolsWithCapabilities.initError,
        toolsCapabilitiesData: toolsWithCapabilities.data,
        toolsCapabilitiesError: toolsWithCapabilities.error,
        toolsFallbackData: toolsFallback.data,
        hasToolsCapability: toolsWithCapabilities.hasToolsCapability,
      };
    }
    return null;
  }, [session, isDeployed, toolsWithCapabilities, toolsFallback]);

  // Use capability-aware data if available, otherwise fallback
  const tools = toolsWithCapabilities.data ?? toolsFallback.data ?? [];
  const resources =
    resourcesWithCapabilities.data ?? resourcesFallback.data ?? [];
  const prompts = promptsWithCapabilities.data ?? promptsFallback.data ?? [];

  const isLoading =
    projectLoading ||
    toolsWithCapabilities.isLoading ||
    resourcesWithCapabilities.isLoading ||
    promptsWithCapabilities.isLoading ||
    (fallbackEnabled &&
      (toolsFallback.isLoading ||
        resourcesFallback.isLoading ||
        promptsFallback.isLoading));

  // Log debug info in development
  if (
    debugInfo &&
    (tools.length > 0 || resources.length > 0 || prompts.length > 0)
  ) {
    console.log("CompactInspector Debug - Data found:", {
      tools: tools.length,
      resources: resources.length,
      prompts: prompts.length,
      ...debugInfo,
    });
  } else if (debugInfo && isDeployed && !isLoading) {
    console.log("CompactInspector Debug - No data:", debugInfo);
  }

  return {
    tools,
    resources,
    prompts,
    isDeployed,
    isLoading,
    session,
    project,
    // Expose debug info for development
    ...(debugInfo && { debugInfo }),
  };
}
